/** biome-ignore-all lint/suspicious/noCommentText: <I said so> */
/** biome-ignore-all lint/a11y/useSemanticElements: <I said so> */

import { Cpu, Menu, Moon, Sun, X } from "lucide-react";
import { Component, type ErrorInfo, lazy, type ReactNode, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import ConnectWallet from "./components/ConnectWallet";
import DevConsole from "./components/console/DevConsole";
import Footer from "./components/Footer";
import {
    AnimatedIcon,
    LazyCpuIcon,
    LazyMenuIcon,
    LazyMoonIcon,
    LazySunIcon,
    StateAnimatedIcon,
    useAnimatedIconTrigger,
} from "./components/icons/animated";
import ChatHint from "./components/onboarding/ChatHint";
import ConnectHint from "./components/onboarding/ConnectHint";
import { ConsoleProvider } from "./context/ConsoleContext";
import { OnboardingProvider } from "./context/OnboardingContext";
import { TezosProvider, useTezos } from "./context/TezosContext";
import { useRecentActivity } from "./hooks/useRecentActivity";
import Developers from "./pages/Developers";
import Hackers from "./pages/Hackers";
import Home from "./pages/Home";
import Manifesto from "./pages/Manifesto";
import Policies from "./pages/Policies";

const ActivityFeedPanel = lazy(() => import("./components/ActivityFeedPanel"));
const ActivityToastQueue = lazy(() => import("./components/ActivityToastQueue"));
const Skills = lazy(() => import("./pages/Skills"));
const SkillDetail = lazy(() => import("./pages/SkillDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const ProjectPage = lazy(() => import("./pages/ProjectPage"));
const Chat = lazy(() => import("./components/chat/ChatPage"));
const WikiApp = lazy(() => import("./pages/WikiApp"));
const Arcade = lazy(() => import("./pages/Arcade"));
const Labs = lazy(() => import("./pages/Labs"));
const LabDetail = lazy(() => import("./pages/LabDetail"));
const ColdMilk = lazy(() => import("./pages/labs/ColdMilk"));
const MatterReaper = lazy(() => import("./pages/labs/MatterReaper"));
const XRay = lazy(() => import("./pages/labs/XRay"));
const Gaspedal = lazy(() => import("./pages/labs/Gaspedal"));
const BulkRelist = lazy(() => import("./pages/labs/BulkRelist"));
const Art4LifeTez = lazy(() => import("./pages/labs/Art4LifeTez"));
const Fa2Deployer = lazy(() => import("./pages/labs/Fa2Deployer"));
const IcoCommitments = lazy(() => import("./pages/labs/IcoCommitments"));

interface ErrorBoundaryState {
    hasError: boolean;
    message: string;
    /** True when the failure looks like a stale lazy chunk after a deploy. */
    stale: boolean;
}

/** Every browser's phrasing for "the lazy chunk you asked for is gone". */
const STALE_CHUNK_RE =
    /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|expected a javascript(-or-wasm)? module/i;

/** Guards against a reload loop if the fresh HTML is broken too. */
const RELOAD_FLAG = "hacktez:chunk-reload";

function isStaleChunkError(err: Error): boolean {
    return STALE_CHUNK_RE.test(err?.message ?? "");
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false, message: "", stale: false };

    static getDerivedStateFromError(err: Error): ErrorBoundaryState {
        return { hasError: true, message: err.message, stale: isStaleChunkError(err) };
    }

    componentDidCatch(err: Error, info: ErrorInfo) {
        if (import.meta.env.DEV) {
            console.error("[hack.tez] Unhandled render error:", err, info.componentStack);
        }

        // A deploy rehashes every lazy chunk, so a tab open across one asks for
        // a file that no longer exists. The module graph caches that rejection,
        // so re-rendering can never recover — only a fresh document can. Reload
        // once, and only once, so a genuinely broken build doesn't spin.
        if (!isStaleChunkError(err)) return;
        try {
            if (sessionStorage.getItem(RELOAD_FLAG)) return;
            sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
        } catch {
            // sessionStorage can throw in private mode — fall through to the UI.
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div
                    role="alert"
                    style={{
                        padding: "2rem",
                        fontFamily: "monospace",
                        background: "var(--bg, #000)",
                        color: "var(--err, #ff6b6b)",
                        border: "1px solid var(--err, #ff6b6b)",
                        margin: "2rem auto",
                        maxWidth: "600px",
                    }}
                >
                    <h1 style={{ fontSize: "1rem", marginBottom: "1rem" }}>
                        {this.state.stale ? "// NEW VERSION AVAILABLE" : "// SYSTEM ERROR"}
                    </h1>
                    <p style={{ color: "var(--fg, #fff)", marginBottom: "1.5rem" }}>
                        {this.state.stale
                            ? "hack.tez was updated while this tab was open. Reload to get the latest version."
                            : this.state.message || "Something went wrong. Please reload."}
                    </p>
                    <button
                        type="button"
                        // Clearing state can't fix a dead chunk — the failed import
                        // is cached for the life of the document. Reload instead.
                        onClick={() => {
                            if (this.state.stale) {
                                try {
                                    sessionStorage.removeItem(RELOAD_FLAG);
                                } catch {
                                    // ignore — reload regardless
                                }
                                window.location.reload();
                                return;
                            }
                            this.setState({ hasError: false, message: "", stale: false });
                        }}
                        style={{
                            background: "var(--fg, #fff)",
                            color: "var(--bg, #000)",
                            border: "none",
                            padding: "0.5rem 1.5rem",
                            fontFamily: "monospace",
                            cursor: "pointer",
                        }}
                    >
                        {this.state.stale ? "RELOAD" : "RETRY"}
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

type Theme = "auto" | "dark" | "light";

function useTheme() {
    const [theme, setThemeState] = useState<Theme>(() => {
        if (typeof localStorage === "undefined") return "auto";
        return (localStorage.getItem("hack-tez-theme") as Theme) ?? "auto";
    });

    useEffect(() => {
        const root = document.documentElement;
        if (theme === "auto") {
            root.removeAttribute("data-theme");
        } else {
            root.setAttribute("data-theme", theme);
        }
        localStorage.setItem("hack-tez-theme", theme);
    }, [theme]);

    return { theme, setTheme: setThemeState };
}

function ThemeSwitcher({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
    const autoTrigger = useAnimatedIconTrigger();
    const darkTrigger = useAnimatedIconTrigger();
    const lightTrigger = useAnimatedIconTrigger();

    return (
        <div className="theme-switcher" role="group" aria-label="Color theme">
            <button
                type="button"
                className="theme-btn"
                aria-pressed={theme === "auto"}
                onClick={() => setTheme("auto")}
                title="Auto (system default)"
                aria-label="Auto"
                {...autoTrigger.handlers}
            >
                <AnimatedIcon
                    ref={autoTrigger.iconRef}
                    Lazy={LazyCpuIcon}
                    fallback={<Cpu size={14} aria-hidden="true" />}
                    size={14}
                />
            </button>
            <button
                type="button"
                className="theme-btn"
                aria-pressed={theme === "dark"}
                onClick={() => setTheme("dark")}
                title="Dark mode"
                aria-label="Dark"
                {...darkTrigger.handlers}
            >
                <AnimatedIcon
                    ref={darkTrigger.iconRef}
                    Lazy={LazyMoonIcon}
                    fallback={<Moon size={14} aria-hidden="true" />}
                    size={14}
                />
            </button>
            <button
                type="button"
                className="theme-btn"
                aria-pressed={theme === "light"}
                onClick={() => setTheme("light")}
                title="Light mode"
                aria-label="Light"
                {...lightTrigger.handlers}
            >
                <AnimatedIcon
                    ref={lightTrigger.iconRef}
                    Lazy={LazySunIcon}
                    fallback={<Sun size={14} aria-hidden="true" />}
                    size={14}
                />
            </button>
        </div>
    );
}

function Nav({ theme, setTheme }: { theme: Theme; setTheme: (t: Theme) => void }) {
    const [open, setOpen] = useState(false);
    const { domain } = useTezos();

    return (
        <nav className="nav" aria-label="Site navigation">
            <div className="container nav-inner">
                <a href="/" className="nav-logo" aria-label="hack.tez home">
                    HACK<span className="dot-tez">TEZ</span>
                </a>

                <div className="nav-actions">
                    <ThemeSwitcher theme={theme} setTheme={setTheme} />
                    <ConnectWallet />
                    <div className="nav-menu">
                        <button
                            type="button"
                            className="nav-hamburger"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            aria-controls="nav-drawer"
                            onClick={() => setOpen((o) => !o)}
                        >
                            <StateAnimatedIcon
                                Lazy={LazyMenuIcon}
                                active={open}
                                fallback={<Menu size={24} aria-hidden="true" />}
                                fallbackActive={<X size={24} aria-hidden="true" />}
                                size={24}
                            />
                        </button>
                        {open && (
                            <div
                                id="nav-drawer"
                                className="nav-drawer"
                                role="menu"
                                aria-label="Navigation menu"
                                onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
                            >
                                <div className="nav-drawer-inner">
                                    <a
                                        href="/"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Home
                                    </a>
                                    {domain && (
                                    <a
                                        href="/chat"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Chat
                                    </a>
                                    )}
                                    <a
                                        href="/arcade"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Arcade
                                    </a>
                                    <a
                                        href="/labs"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Labs
                                    </a>
                                    <a
                                        href="/wiki"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Wiki
                                    </a>
                                    <a
                                        href="/hackers"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Hackers
                                    </a>
                                    <a
                                        href="/developers"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Docs
                                    </a>
                                    <a
                                        href="/skills"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Skills
                                    </a>
                                    <a
                                        href="/policies"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Policies
                                    </a>
                                    <a
                                        href="/manifesto"
                                        role="menuitem"
                                        className="nav-drawer-link"
                                        onClick={() => setOpen(false)}
                                    >
                                        Manifesto
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}

function ActivityLayer() {
    const location = useLocation();
    const { domain, restoring } = useTezos();
    const { events, newEvents, isLoading } = useRecentActivity();
    // Only show on the landing page (unauthenticated homepage)
    if (location.pathname !== "/") return null;
    if (domain || restoring) return null;
    return (
        <>
            {/* Desktop: fixed side panel (lg+ only, hidden via CSS on smaller screens) */}
            <Suspense fallback={null}>
                <ActivityFeedPanel events={events} isLoading={isLoading} />
            </Suspense>
            {/* Mobile: toast queue (hidden on lg+ via CSS) */}
            <Suspense fallback={null}>
                <ActivityToastQueue newEvents={newEvents} />
            </Suspense>
        </>
    );
}

export function AppShell() {
    const { theme, setTheme } = useTheme();
    const location = useLocation();
    const isChat = location.pathname === "/chat";
    const isWiki = location.pathname.startsWith("/wiki");

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
            {/* Noise grain overlay — decorative, hidden from AT */}
            <div className="noise-overlay" aria-hidden="true" />

            {/* Skip to main content link */}
            <a href="#main-content" className="skip-link">
                Skip to content
            </a>

            {/* Navigation */}
            <Nav theme={theme} setTheme={setTheme} />

            {/* Main content */}
            <main
                id="main-content"
                tabIndex={-1}
                style={isChat
                    ? { flex: "1 1 0", minHeight: 0, display: "flex", flexDirection: "column" as const, overflow: "hidden" }
                    : { flex: "1 0 auto" }
                }
            >
                <Routes>
                    <Route path="/manifesto" element={<Manifesto />} />
                    <Route path="/hackers" element={<Hackers />} />
                    <Route path="/developers" element={<Developers />} />
                    <Route path="/policies" element={<Policies />} />
                    <Route path="/skills/:slug" element={<Suspense fallback={null}><SkillDetail /></Suspense>} />
                    <Route path="/skills" element={<Suspense fallback={null}><Skills /></Suspense>} />
                    <Route path="/u/:subdomain" element={<Suspense fallback={null}><Profile /></Suspense>} />
                    <Route path="/u/:subdomain/p/:project" element={<Suspense fallback={null}><ProjectPage /></Suspense>} />
                    <Route path="/manage" element={<Navigate to="/" replace />} />
                    <Route path="/chat" element={<Suspense fallback={null}><Chat /></Suspense>} />
                    <Route path="/wiki/*" element={<Suspense fallback={null}><WikiApp /></Suspense>} />
                    <Route path="/arcade/*" element={<Suspense fallback={null}><Arcade /></Suspense>} />
                    <Route path="/labs" element={<Suspense fallback={null}><Labs /></Suspense>} />
                    <Route path="/labs/coldmilk" element={<Suspense fallback={null}><ColdMilk /></Suspense>} />
                    <Route path="/labs/matter-reaper" element={<Suspense fallback={null}><MatterReaper /></Suspense>} />
                    <Route path="/labs/x-ray" element={<Suspense fallback={null}><XRay /></Suspense>} />
                    <Route path="/labs/gaspedal" element={<Suspense fallback={null}><Gaspedal /></Suspense>} />
                    <Route path="/labs/bulk-relist" element={<Suspense fallback={null}><BulkRelist /></Suspense>} />
                    <Route path="/labs/art4lifetez" element={<Suspense fallback={null}><Art4LifeTez /></Suspense>} />
                    <Route path="/labs/fa2-deployer" element={<Suspense fallback={null}><Fa2Deployer /></Suspense>} />
                    <Route path="/labs/ico-commitments" element={<Suspense fallback={null}><IcoCommitments /></Suspense>} />
                    <Route path="/labs/:slug" element={<Suspense fallback={null}><LabDetail /></Suspense>} />
                    <Route path="/" element={<Home />} />
                    <Route path="*" element={<Home />} />
                </Routes>
            </main>

            <ActivityLayer />
            <Footer compact={isChat || isWiki} />
            <ConnectHint />
            <ChatHint />
            <DevConsole />
        </div>
    );
}

export default function App() {
    // The app rendered, so whatever chunk failed before is resolved. Release
    // the one-shot guard so a future deploy can auto-recover this tab too.
    useEffect(() => {
        try {
            sessionStorage.removeItem(RELOAD_FLAG);
        } catch {
            // sessionStorage unavailable — the guard simply stays as-is.
        }
    }, []);

    return (
        <ErrorBoundary>
            <TezosProvider>
                <BrowserRouter>
                    <OnboardingProvider>
                        <ConsoleProvider>
                            <AppShell />
                        </ConsoleProvider>
                    </OnboardingProvider>
                </BrowserRouter>
            </TezosProvider>
        </ErrorBoundary>
    );
}
